"use client";

import clsx from "clsx";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import Input from "@/components/library/Input";
import { CallDirection, CallResolution } from "@/enums/dashboard";
import { useOrg } from "@/hooks/useOrg";
import { AgencyNotificationRequest } from "@/types/agencies";
import { apiFetch } from "@/lib/api";
import { EMAIL_REQUIRED_REGEX } from "@/utils/common/regex";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { StepFooter } from "./StepFooter";

interface ConfirmNotificationsStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
}

// Default notification settings for completed calls (non-voicemail)
const DEFAULT_NOTIFICATION_SETTINGS: AgencyNotificationRequest["settings"] = {
  directions: [CallDirection.Inbound, CallDirection.Outbound],
  resolutions: [CallResolution.Yes, CallResolution.Transfer],
  include_voicemail: false,
  include_campaign_call: true,
  call_category: [],
};

const DEFAULT_MESSAGING_SETTINGS: AgencyNotificationRequest["messagingSettings"] = {
  conversation_started: false,
  conversation_updated: false,
  chat_conversation_started: false,
  chat_conversation_updated: false,
};

type EmailStatus = "pending" | "saved" | "failed";

interface EmailEntry {
  id: string;
  value: string;
  status: EmailStatus;
}

export function ConfirmNotificationsStep({ onBack, onNext }: ConfirmNotificationsStepProps) {
  const { t } = useTranslation("onboarding");
  const { org } = useOrg();

  // Pre-fill with owner's email
  const ownerEmail = org?.owner?.email || "";

  const nextId = useRef(1);
  const [entries, setEntries] = useState<EmailEntry[]>([
    { id: "email-0", value: ownerEmail, status: "pending" },
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const addEmailField = useCallback(() => {
    const id = `email-${nextId.current++}`;
    setEntries((prev) => [...prev, { id, value: "", status: "pending" }]);
  }, []);

  const updateEmail = useCallback((id: string, value: string) => {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, value, status: "pending" } : entry))
    );
  }, []);

  const removeEmailField = useCallback((id: string) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const handleSave = useCallback(async () => {
    setHasSubmitted(true);

    // Only submit emails that haven't been saved yet
    const pendingEntries: { id: string; trimmed: string }[] = [];

    for (const entry of entries) {
      if (entry.status === "saved") continue;
      const trimmed = entry.value.trim();
      if (!trimmed) continue; // skip empty fields
      pendingEntries.push({ id: entry.id, trimmed });
    }

    // Validate pending emails
    const hasInvalidEmail = pendingEntries.some((e) => !EMAIL_REQUIRED_REGEX.test(e.trimmed));
    if (hasInvalidEmail) {
      return;
    }

    // If nothing to submit (all already saved or all empty), just proceed
    if (pendingEntries.length === 0) {
      const hasSaved = entries.some((e) => e.status === "saved");
      if (hasSaved) {
        onNext();
      }
      return;
    }

    // Deduplicate case-insensitively while preserving original casing
    const uniqueMap = new Map<string, { email: string; ids: string[] }>();
    for (const { id, trimmed } of pendingEntries) {
      const lower = trimmed.toLowerCase();
      const existing = uniqueMap.get(lower);
      if (existing) {
        existing.ids.push(id);
      } else {
        uniqueMap.set(lower, { email: trimmed, ids: [id] });
      }
    }

    setIsSaving(true);
    try {
      const uniqueGroups = Array.from(uniqueMap.values());

      const results = await Promise.allSettled(
        uniqueGroups.map(({ email }) =>
          apiFetch<unknown>(
            "/api/settings/agency/notifications",
            "POST",
            {
            body: {
            email,
            settings: DEFAULT_NOTIFICATION_SETTINGS,
            messagingSettings: DEFAULT_MESSAGING_SETTINGS,
          },
          },
          ).then((res) => {
            if (!res) throw new Error("Empty response");
            return res;
          })
        )
      );

      // Build a map of entry id → new status
      const statusById = new Map<string, EmailStatus>();
      for (let i = 0; i < uniqueGroups.length; i++) {
        const status: EmailStatus = results[i].status === "fulfilled" ? "saved" : "failed";
        for (const id of uniqueGroups[i].ids) {
          statusById.set(id, status);
        }
      }

      setEntries((prev) =>
        prev.map((entry) => {
          const newStatus = statusById.get(entry.id);
          return newStatus ? { ...entry, status: newStatus } : entry;
        })
      );

      const allSucceeded = results.every((r) => r.status === "fulfilled");
      if (allSucceeded) {
        onNext();
      }
      // If some failed, stay on the page — user can retry just the failed ones
    } catch (err) {
      console.error("Failed to save notification settings", err);
    } finally {
      setIsSaving(false);
    }
  }, [entries, onNext]);

  const handleSkip = useCallback(() => {
    onNext();
  }, [onNext]);

  const hasFailed = entries.some((e) => e.status === "failed");
  const hasSaved = entries.some((e) => e.status === "saved");
  const allSaved = entries.every((e) => e.status === "saved" || e.value.trim() === "");

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center">
        <div className="w-full max-w-lg space-y-8">
          {/* Header */}
          <OnboardingStepHeader
            title={t("confirmNotifications.title")}
            description={t("confirmNotifications.subtitle")}
          />

          {/* Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Icon icon="bell" className="w-10 h-10 text-primary" />
            </div>
          </div>

          {/* Email Input */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-foreground">
                {t("confirmNotifications.emailLabel")}
              </p>
              <Button
                type="none"
                onClick={addEmailField}
                disabled={isSaving}
                iconBefore={<Icon icon="plus" className="w-4 h-4" />}
                compact
                className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50"
              >
                {t("confirmNotifications.addMore")}
              </Button>
            </div>

            <div className="space-y-3">
              {entries.map((entry, index) => {
                const isSavedEntry = entry.status === "saved";
                const isFailedEntry = entry.status === "failed";
                const isFirst = index === 0;
                const showValidationError =
                  hasSubmitted &&
                  !isSavedEntry &&
                  entry.value.trim() !== "" &&
                  !EMAIL_REQUIRED_REGEX.test(entry.value.trim());

                return (
                  <div key={entry.id}>
                    <div className="relative flex items-center gap-2">
                      <div className="flex-1 relative">
                        <Input
                          type="email"
                          value={entry.value}
                          onChange={(e) => updateEmail(entry.id, e.target.value)}
                          placeholder={
                            isFirst
                              ? t("confirmNotifications.emailPlaceholder")
                              : "teammate@email.com"
                          }
                          required
                          error={showValidationError || isFailedEntry}
                          icon={<Icon icon="envelope" className="w-4 h-4 text-muted-foreground" />}
                          disabled={isSavedEntry || isSaving}
                          className={clsx(
                            isSavedEntry && "opacity-70",
                            !isFirst && !isSavedEntry && "[&_input]:pr-10"
                          )}
                        />
                        {!isFirst && !isSavedEntry && (
                          <button
                            type="button"
                            onClick={() => removeEmailField(entry.id)}
                            disabled={isSaving}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-500 hover:text-destructive transition-colors disabled:opacity-50"
                            aria-label="Remove email"
                          >
                            <Icon icon="trash" className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {isSavedEntry && (
                        <Icon icon="circle-check" className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      )}
                      {isFailedEntry && (
                        <Icon icon="circle-exclamation" className="w-5 h-5 text-destructive flex-shrink-0" />
                      )}
                    </div>
                    {isFailedEntry && (
                      <p className="text-xs text-destructive mt-1 ml-1">
                        {t("confirmNotifications.emailFailed")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* What you'll receive */}
          <div className="rounded-xl border border-200 bg-panel p-4 space-y-3">
            <h3 className="text-sm font-medium text-foreground">
              {t("confirmNotifications.whatYouGet.title")}
            </h3>
            <ul className="space-y-2">
              {(["completedCalls", "transferredCalls", "bothDirections"] as const).map((key) => (
                <li key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon icon="check" className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span>{t(`confirmNotifications.whatYouGet.${key}`)}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-400 pt-1">{t("confirmNotifications.customizeNote")}</p>
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <StepFooter
        onBack={onBack}
        onSkip={!allSaved ? handleSkip : undefined}
        skipLabel={hasSaved ? t("wizard.continueAnyway") : undefined}
        onNext={allSaved ? onNext : handleSave}
        nextLabel={
          allSaved ? t("wizard.next") : hasFailed ? t("wizard.retry") : t("wizard.confirm")
        }
        isSaving={isSaving}
      />
    </div>
  );
}
